import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Star,
  Plus,
  Loader2,
  Quote,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  ImagePlus,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_REVIEW_IMAGES = 6;

const resolveImageUrl = (url) =>
  url.startsWith("/uploads") ? `${BACKEND_URL}${url}` : url;

const StarRow = ({ value, onChange, size = 22, interactive = false }) => {
  const [hover, setHover] = useState(0);
  const stars = [1, 2, 3, 4, 5];
  return (
    <div className="flex items-center gap-1.5">
      {stars.map((n) => {
        const filled = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            disabled={!interactive}
            onMouseEnter={() => interactive && setHover(n)}
            onMouseLeave={() => interactive && setHover(0)}
            onClick={() => interactive && onChange?.(n)}
            data-testid={interactive ? `rating-star-${n}` : undefined}
            className={`${interactive ? "cursor-pointer" : "cursor-default"} p-0 bg-transparent border-0`}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
          >
            <Star
              size={size}
              strokeWidth={1.4}
              className={filled ? "text-brand-sand" : "text-brand-line"}
              fill={filled ? "#D8C3A5" : "transparent"}
            />
          </button>
        );
      })}
    </div>
  );
};

const ReviewCard = ({ r, onImageClick }) => (
  <article
    data-testid={`review-card-${r.id}`}
    className="bg-white border border-brand-line p-8 md:p-10 flex flex-col"
  >
    <StarRow value={r.rating} size={18} />
    <p className="mt-6 text-brand-ink leading-relaxed text-base font-body flex-1">
      &ldquo;{r.text}&rdquo;
    </p>
    {r.images?.length > 0 && (
      <div
        data-testid={`review-card-images-${r.id}`}
        className="mt-6 flex flex-wrap gap-2"
      >
        {r.images.map((src, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onImageClick?.(resolveImageUrl(src))}
            className="w-16 h-16 overflow-hidden bg-brand-surfaceAlt border border-brand-line"
          >
            <img
              src={resolveImageUrl(src)}
              alt={`${r.name} review photo ${i + 1}`}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            />
          </button>
        ))}
      </div>
    )}
    <div className="mt-8 pt-6 border-t border-brand-line">
      <p className="font-heading text-lg text-brand-ink">{r.name}</p>
      {r.suburb && (
        <p className="text-xs tracking-[0.15em] uppercase text-brand-inkMuted mt-1">
          {r.suburb}
        </p>
      )}
    </div>
  </article>
);

const initial = { name: "", suburb: "", rating: 0, text: "", images: [] };
const PAGE_SIZE = 10;

/**
 * Reviews block.
 * mode="home" → top 6 by rating, button to /reviews + count
 * mode="all"  → every review
 */
export default function Reviews({ mode = "home" }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      setReviews(res.data || []);
    } catch (e) {
      console.error("Failed to load reviews", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const total = reviews.length;
  // Top 6: sort by rating desc, then most recent first
  const topReviews = [...reviews]
    .sort((a, b) => {
      if (b.rating !== a.rating) return b.rating - a.rating;
      return new Date(b.created_at) - new Date(a.created_at);
    })
    .slice(0, 6);

  const totalPages = Math.max(1, Math.ceil(reviews.length / PAGE_SIZE));
  const pagedReviews = reviews.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const visible = mode === "all" ? pagedReviews : topReviews;

  const goToPage = (n) => {
    setPage(Math.min(Math.max(1, n), totalPages));
    if (mode === "all") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const update = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    const room = MAX_REVIEW_IMAGES - form.images.length;
    if (room <= 0) {
      toast.error(`You can add up to ${MAX_REVIEW_IMAGES} photos.`);
      return;
    }
    setUploadingImages(true);
    try {
      const uploads = await Promise.all(
        files.slice(0, room).map(async (file) => {
          const formData = new FormData();
          formData.append("file", file);
          const res = await axios.post(`${API}/reviews/upload-image`, formData);
          return res.data.url;
        })
      );
      setForm((p) => ({ ...p, images: [...p.images, ...uploads] }));
    } catch (err) {
      console.error(err);
      toast.error("Couldn't upload one or more photos.");
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (idx) =>
    setForm((p) => ({ ...p, images: p.images.filter((_, i) => i !== idx) }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.rating || !form.text || form.text.length < 4) {
      toast.error("Please add your name, a rating and a short review.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("Thanks for the review — much appreciated!");
      setForm(initial);
      setShowForm(false);
      fetchReviews();
    } catch (err) {
      console.error(err);
      toast.error("Couldn't submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid={mode === "all" ? "section-all-reviews" : "section-reviews"}
      className={mode === "all" ? "" : "bg-brand-surfaceAlt nv-section"}
    >
      <div className="nv-container">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-7">
            <div className="flex items-center gap-4 mb-5">
              <p className="nv-overline">Reviews</p>
              {!loading && total > 0 && (
                <span
                  data-testid="reviews-count"
                  className="text-xs tracking-[0.15em] uppercase text-brand-inkMuted"
                >
                  {total} {total === 1 ? "review" : "reviews"}
                </span>
              )}
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl leading-tight">
              {mode === "all"
                ? "Every review, in one place."
                : "What Adelaide homeowners are saying."}
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9 flex md:justify-end md:items-end">
            <button
              type="button"
              onClick={() => setShowForm((v) => !v)}
              data-testid="reviews-toggle-form"
              className="nv-btn-primary"
            >
              <Plus size={16} />
              {showForm ? "Close" : "Leave a review"}
            </button>
          </div>
        </div>

        {showForm && (
          <form
            onSubmit={onSubmit}
            data-testid="review-form"
            className="bg-white border border-brand-line p-8 md:p-10 mb-12 space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="rname">Your name *</Label>
                <Input
                  id="rname"
                  data-testid="review-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="First name"
                  className="rounded-none border-brand-line focus-visible:ring-brand-navy focus-visible:border-brand-navy"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rsuburb">Suburb (optional)</Label>
                <Input
                  id="rsuburb"
                  data-testid="review-suburb"
                  value={form.suburb}
                  onChange={update("suburb")}
                  placeholder="e.g. Glenelg"
                  className="rounded-none border-brand-line focus-visible:ring-brand-navy focus-visible:border-brand-navy"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Your rating *</Label>
              <StarRow
                value={form.rating}
                onChange={(n) => setForm((p) => ({ ...p, rating: n }))}
                interactive
                size={28}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rtext">Your review *</Label>
              <Textarea
                id="rtext"
                data-testid="review-text"
                value={form.text}
                onChange={update("text")}
                placeholder="What was the job, how did it go, and would you recommend NikoVision?"
                rows={4}
                className="rounded-none border-brand-line focus-visible:ring-brand-navy focus-visible:border-brand-navy resize-none"
              />
            </div>

            <div className="space-y-2">
              <Label>Add photos (optional)</Label>
              <div className="flex flex-wrap gap-3">
                {form.images.map((src, i) => (
                  <div
                    key={i}
                    className="relative w-20 h-20 border border-brand-line group"
                  >
                    <img
                      src={resolveImageUrl(src)}
                      alt={`Upload ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      aria-label="Remove photo"
                      data-testid={`review-image-remove-${i}`}
                      className="absolute -top-2 -right-2 bg-brand-ink text-white rounded-full p-1"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {form.images.length < MAX_REVIEW_IMAGES && (
                  <label
                    htmlFor="review-images"
                    data-testid="review-image-upload"
                    className="w-20 h-20 flex flex-col items-center justify-center gap-1 border border-dashed border-brand-line text-brand-inkMuted cursor-pointer hover:border-brand-navy hover:text-brand-navy transition-colors"
                  >
                    {uploadingImages ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <>
                        <ImagePlus size={18} />
                        <span className="text-[10px]">Add</span>
                      </>
                    )}
                    <input
                      id="review-images"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageSelect}
                      disabled={uploadingImages}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>

            <button
              type="submit"
              data-testid="review-submit"
              disabled={submitting}
              className="nv-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Submitting...
                </>
              ) : (
                "Submit review"
              )}
            </button>
          </form>
        )}

        {loading ? (
          <p className="text-brand-inkMuted">Loading reviews…</p>
        ) : visible.length === 0 ? (
          <div
            data-testid="reviews-empty"
            className="bg-white border border-dashed border-brand-line p-12 text-center"
          >
            <Quote
              size={32}
              strokeWidth={1.2}
              className="mx-auto text-brand-sand mb-6"
            />
            <h3 className="font-heading text-2xl">No reviews yet — be the first.</h3>
            <p className="mt-3 text-brand-inkMuted text-sm max-w-md mx-auto">
              If we&apos;ve done a job for you, we&apos;d be grateful for a
              few honest words. It helps other Adelaide homeowners find us.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visible.map((r) => (
                <ReviewCard key={r.id} r={r} onImageClick={setLightbox} />
              ))}
            </div>

            {mode === "home" && total > 6 && (
              <div className="mt-14 flex justify-center">
                <Link
                  to="/reviews"
                  data-testid="view-all-reviews"
                  className="nv-btn-secondary"
                >
                  View all {total} reviews <ArrowUpRight size={16} />
                </Link>
              </div>
            )}

            {mode === "all" && totalPages > 1 && (
              <nav
                data-testid="reviews-pagination"
                aria-label="Reviews pagination"
                className="mt-14 flex items-center justify-center gap-2"
              >
                <button
                  type="button"
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 1}
                  data-testid="reviews-page-prev"
                  aria-label="Previous page"
                  className="p-2 border border-brand-line text-brand-ink disabled:opacity-30 disabled:cursor-not-allowed hover:border-brand-navy transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => goToPage(n)}
                    data-testid={`reviews-page-${n}`}
                    aria-current={n === page ? "page" : undefined}
                    className={`w-9 h-9 text-sm border transition-colors ${
                      n === page
                        ? "bg-brand-navy text-white border-brand-navy"
                        : "border-brand-line text-brand-ink hover:border-brand-navy"
                    }`}
                  >
                    {n}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={() => goToPage(page + 1)}
                  disabled={page === totalPages}
                  data-testid="reviews-page-next"
                  aria-label="Next page"
                  className="p-2 border border-brand-line text-brand-ink disabled:opacity-30 disabled:cursor-not-allowed hover:border-brand-navy transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </nav>
            )}
          </>
        )}
      </div>

      {lightbox && (
        <div
          data-testid="review-lightbox"
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-12 animate-fade-in"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            data-testid="review-lightbox-close"
            onClick={() => setLightbox(null)}
            aria-label="Close"
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2"
          >
            <X size={28} />
          </button>
          <img
            src={lightbox}
            alt="Review photo"
            className="max-w-full max-h-full object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
